import { action, computed, observable } from 'mobx'
import { services, interfaces, models } from '@tfo/common'
import $http from '../utils/$http';
import { BaseCheckpointModel, CheckpointCategory, Status as ApprovalStatus } from '@tfo/common/src/models';
import { Language } from '../models/Enums';
export interface ReportStoreSectionProps {
	id: string
	label: string
}

interface FileMetaData {
	id: number
	originalFilename: string
	filePurposeType: string
}
interface Report {
	individual: {
		firstName: string
		lastName: string
		individualId: string
	}
	creator: {
		firstName: string
		lastName: string
		companyName: string
	}
	order: {
		created: Date
		delivered: Date
		researchStarted: Date
		isInternationalOrder: boolean
		isIDD: boolean
		receiptNumber: string
		[checkpointCategory: string]: { type: string } & any //missing proper type for now
		approvalActions: [
			{
				actionDate: Date
				status: ApprovalStatus
			}
		]
		checkpoints: Array<BaseCheckpointModel>
		checkpointTypes: Array<string>
		fileMetaData: Array<FileMetaData>
		id: number,
		language: Language
	}
}

export class ReportStore implements interfaces.IReportStore {
	private hasCollectedCheckpoints: boolean = false

	@observable loading: boolean = false
	@observable section: string
	@observable initialDataLoaded: boolean = false

	@observable order: models.IOrderClient & { fileMetaData: Array<FileMetaData> }
	@observable language: Language = 'Svenska'

	@observable
	nameAndNationalIdCheckpoint: models.PersonaliaModels.NameAndNationalIdCheckpoint
	@observable nameChangeCheckpoint: models.PersonaliaModels.NameChangeCheckpoint
	@observable childrenCheckpoint: models.PersonaliaModels.ChildrenCheckpoint
	@observable
	driversLicenseCheckpoint: models.PersonaliaModels.DriversLicenseCheckpoint
	@observable
	residentialAddressCheckpoint: models.PersonaliaModels.ResidentialAddressCheckpoint
	@observable
	residentialAddressHistoryCheckpoint: models.PersonaliaModels.ResidentialAddressHistoryCheckpoint
	@observable maritalStatusCheckpoint: models.PersonaliaModels.MaritalStatusCheckpoint
	@observable migrationCheckpoint: models.PersonaliaModels.MigrationCheckpoint
	@observable
	militaryServiceCheckpoint: models.PersonaliaModels.MilitaryServiceCheckpoint
	@observable
	cohabitantsCheckpoint: models.PersonaliaModels.CohabitantsCheckpoint

	@observable
	personalDebtCheckpoint: models.FinancialModels.PersonalDebtCheckpoint
	@observable
	studentDebtCheckpoint: models.FinancialModels.StudentDebtCheckpoint
	@observable
	taxableIncomeCheckpoint: models.FinancialModels.TaxableIncomeCheckpoint
	@observable
	vehicleOwnershipCheckpoint: models.FinancialModels.VehicleOwnershipCheckpoint
	@observable
	paymentDefaultCheckpoint: models.FinancialModels.PaymentDefaultCheckpoint
	@observable
	corporateEngagementCheckpoint: models.FinancialModels.CorporateEngagementCheckpoint
	@observable extendedCorporateEngagementMappingCheckpoint: models.FinancialModels.ExtendedCorporateEngagementMappingCheckpoint
	@observable
	seizureAttemptsCheckpoint: models.FinancialModels.SeizureAttemptsCheckpoint
	@observable
	personalBankruptcyCheckpoint: models.FinancialModels.PersonalBankruptcyCheckpoint
	@observable
	propertyOwnershipCheckpoint: models.FinancialModels.PropertyOwnershipCheckpoint
	@observable
	banOnBusinessAcitvityCheckpoint: models.FinancialModels.BanOnBusinessActivtyCheckpoint
	@observable
	extendedFinancialMappingActivtyCheckpoint: models.FinancialModels.ExtendedFinancialMappingActivtyCheckpoint
	@observable
	publicTrusteeshipCheckpoint: models.FinancialModels.PublicTrusteeshipCheckpoint
	@observable partnerBackgroundCheckCheckpoint: models.FinancialModels.PartnerBackgroundCheckCheckpoint
	@observable
	soleproprietorshipCheckpoint: models.FinancialModels.SoleProprietorshipCheckpoint
	@observable
	representationProhobition: models.FinancialModels.RepresentationProhibitionCheckpoint
	@observable
	beneficialOwnerCheckpoint: models.FinancialModels.BeneficialOwnerCheckpoint

	@observable districtCourtsCheckpoint: models.LegalModels.CourtCheckpoint
	@observable courtsofAppealCheckpoint: models.LegalModels.CourtCheckpoint
	@observable administrativeCourtsCheckpoint: models.LegalModels.CourtCheckpoint
	@observable sanctionsListCheckpoint: models.LegalModels.SanctionsListCheckpoint
	@observable pepListCheckpoint: models.LegalModels.PEPCheckpoint

	@observable
	employmentCheckpoint: models.ResumeModels.EmploymentCheckpoint
	@observable
	currentEmploymentCheckpoint: models.ResumeModels.EmploymentCheckpoint
	@observable
	educationCheckpoint: models.ResumeModels.EducationCheckpoint
	@observable
	highestEducationCheckpoint: models.ResumeModels.EducationCheckpoint
	@observable
	referencesCheckpoint: models.ResumeModels.ReferencesCheckpoint
	@observable
	otherMeritsCheckpoint: models.ResumeModels.OtherMeritsCheckpoint

	@observable internetAndMediaCheckpoint: models.MediaModels.InternetAndMediaCheckpoint

	@observable archivedCheckpoints: models.BaseCheckpointModel[]

	@observable activeReportPackages: models.ReportPackage[]
	@observable company: models.ICompany
	@observable reportPackageName: string
	@observable isIDD: boolean
	@observable depersonalizedOrderInformation: interfaces.DepersonalizedOrderInfo | null = null

	network: services.Network
	userStore: interfaces.UserStore

	constructor({ network, userStore }) {
		this.userStore = userStore
		this.network = network
	}

	@action async fetchOrder(orderId: number) {
		this.initialDataLoaded = false

		const report = await $http.get<Report>(`report/${orderId}`)

		this.order = report.order as any
		this.language = report.order.language
		this.company = { name: report.creator.companyName } as any
		this.isIDD = report.order.isIDD
		this.order.candidate = report.individual as any
		this.order.creatorName = `${report.creator.firstName} ${report.creator.lastName}`
		this.order.controlPerson = report.individual.individualId

		//The Common report wants this object in a particular (and stupid) manner, and changing that means more work than current scope.
		//For now, simply convert our beautiful sensible type to this atrocity.
		this.order.checkpointInfo = report.order.checkpointTypes.reduce((accumulator, value) => {
			return { ...accumulator, [`service.checkpoints.${value}`]: `readreport.informationsource.${value}` }
		}, {}) as any



		this.resetCheckpoints()
		if (report.order.checkpoints) {
			Object.keys(report.order.checkpoints).forEach(async checkpointCategory => {
				report.order.checkpoints[checkpointCategory].forEach(async checkpoint => {
					if (this.isArchivedCheckpoint(checkpoint)) {
						this.archivedCheckpoints.push(new models.ArchivedModels.ArchivedCheckpoint(checkpoint))
					}
					else {
						await this.assignToStoreProperties(this.order, checkpoint)
					}
				})
			})
			this.hasCollectedCheckpoints = true
		}

		this.initialDataLoaded = true
	}

	@action resetCheckpoints() {
		this.hasCollectedCheckpoints = false
		this.archivedCheckpoints = []
		this.nameAndNationalIdCheckpoint = undefined
		this.nameChangeCheckpoint = undefined
		this.driversLicenseCheckpoint = undefined
		this.residentialAddressCheckpoint = undefined
		this.residentialAddressHistoryCheckpoint = undefined
		this.migrationCheckpoint = undefined
		this.personalDebtCheckpoint = undefined
		this.studentDebtCheckpoint = undefined
		this.taxableIncomeCheckpoint = undefined
		this.vehicleOwnershipCheckpoint = undefined
		this.paymentDefaultCheckpoint = undefined
		this.corporateEngagementCheckpoint = undefined
		this.seizureAttemptsCheckpoint = undefined
		this.personalBankruptcyCheckpoint = undefined
		this.propertyOwnershipCheckpoint = undefined
		this.employmentCheckpoint = undefined
		this.currentEmploymentCheckpoint = undefined
		this.extendedCorporateEngagementMappingCheckpoint = undefined
		this.educationCheckpoint = undefined
		this.referencesCheckpoint = undefined
		this.otherMeritsCheckpoint = undefined
		this.cohabitantsCheckpoint = undefined
		this.banOnBusinessAcitvityCheckpoint = undefined
		this.extendedFinancialMappingActivtyCheckpoint = undefined
		this.publicTrusteeshipCheckpoint = undefined
		this.partnerBackgroundCheckCheckpoint = undefined
		this.soleproprietorshipCheckpoint = undefined
		this.representationProhobition = undefined
		this.beneficialOwnerCheckpoint = undefined
		this.internetAndMediaCheckpoint = undefined
		this.highestEducationCheckpoint = undefined
	}

	@action public async updateData(order: models.IOrderClient) {
		this.loading = true
		this.fetchOrder(order.id)
		this.loading = false
	}

	@action public async updateDataByOrderId(orderId: number) {
		this.loading = true
		this.fetchOrder(orderId)
		this.loading = false
	}

	private isArchivedCheckpoint(checkpoint: any): boolean {
		return checkpoint.archived;
	}

	private assignToStoreProperties(order: models.IOrderClient, checkpoint: any) {
		if (checkpoint === null) return null

		switch (checkpoint.type) {
			case 'NameAndNationalId':
				this.nameAndNationalIdCheckpoint = new models.PersonaliaModels.NameAndNationalIdCheckpoint(
					checkpoint
				)
				break
			case 'Children':
				this.childrenCheckpoint = new models.PersonaliaModels.ChildrenCheckpoint(
					checkpoint
				)
				break
			case 'ResidentialAddresses':
				this.residentialAddressCheckpoint = new models.PersonaliaModels.ResidentialAddressCheckpoint(
					checkpoint
				)
				break
			case 'Cohabitants':
				this.cohabitantsCheckpoint = new models.PersonaliaModels.CohabitantsCheckpoint(
					checkpoint
				)
				break
			case 'PersonalDebt':
				this.personalDebtCheckpoint = new models.FinancialModels.PersonalDebtCheckpoint(
					checkpoint
				)
				break
			case 'StudentDebt':
				this.studentDebtCheckpoint = new models.FinancialModels.StudentDebtCheckpoint(
					checkpoint
				)
				break
			case 'PaymentDefault':
				this.paymentDefaultCheckpoint = new models.FinancialModels.PaymentDefaultCheckpoint(
					checkpoint
				)
				break
			case 'SeizureAttempts':
				this.seizureAttemptsCheckpoint = new models.FinancialModels.SeizureAttemptsCheckpoint(
					checkpoint
				)
				break
			case 'PersonalBankruptcy':
				this.personalBankruptcyCheckpoint = new models.FinancialModels.PersonalBankruptcyCheckpoint(
					checkpoint
				)
				break
			case 'DistrictCourts':
				this.districtCourtsCheckpoint = new models.LegalModels.CourtCheckpoint(
					checkpoint
				)
				break
			case 'CourtsOfAppeal':
				this.courtsofAppealCheckpoint = new models.LegalModels.CourtCheckpoint(
					checkpoint
				)
				break
			case 'AdministrativeCourts':
				this.administrativeCourtsCheckpoint = new models.LegalModels.CourtCheckpoint(
					checkpoint
				)
				break
			case 'SanctionsList':
				this.sanctionsListCheckpoint = new models.LegalModels.SanctionsListCheckpoint(
					checkpoint
				)
				break
			case 'PoliticallyExposedPerson':
				this.pepListCheckpoint = new models.LegalModels.PEPCheckpoint(checkpoint)
				break
			case 'DriversLicense':
				this.driversLicenseCheckpoint = new models.PersonaliaModels.DriversLicenseCheckpoint(
					checkpoint
				)
				break
			case 'MaritalStatus':
				this.maritalStatusCheckpoint = new models.PersonaliaModels.MaritalStatusCheckpoint(
					checkpoint
				)
				break
			case 'Migration':
				this.migrationCheckpoint = new models.PersonaliaModels.MigrationCheckpoint(
					checkpoint
				)
				break
			case 'MilitaryService':
				this.militaryServiceCheckpoint = new models.PersonaliaModels.MilitaryServiceCheckpoint(
					checkpoint
				)
				break
			case 'VehicleOwnership':
				this.vehicleOwnershipCheckpoint = new models.FinancialModels.VehicleOwnershipCheckpoint(
					checkpoint
				)
				break
			case 'CorporateEngagement':
				this.corporateEngagementCheckpoint = new models.FinancialModels.CorporateEngagementCheckpoint(
					checkpoint
				)
				break
			case models.CheckpointType.BanOnBusinessActivity:
				this.banOnBusinessAcitvityCheckpoint = new models.FinancialModels.BanOnBusinessActivtyCheckpoint(
					checkpoint
				)
				break
			case models.CheckpointType.ExtendedFinancialMapping:
				this.extendedFinancialMappingActivtyCheckpoint = new models.FinancialModels.ExtendedFinancialMappingActivtyCheckpoint(
					checkpoint
				)
				break
			case 'NameChange':
				this.nameChangeCheckpoint = new models.PersonaliaModels.NameChangeCheckpoint(
					checkpoint
				)
				break
			case 'HistoryResidentialAddresses':
				this.residentialAddressHistoryCheckpoint = new models.PersonaliaModels.ResidentialAddressHistoryCheckpoint(
					checkpoint
				)
				break
			case 'TaxableIncomeTwoYears':
			case 'TaxableIncomeThreeYears':
			case 'TaxableIncomeFiveYears':
				this.taxableIncomeCheckpoint = new models.FinancialModels.TaxableIncomeCheckpoint(
					checkpoint
				)
				break
			case 'TaxableIncomeTenYears':
				this.taxableIncomeCheckpoint = new models.FinancialModels.TaxableIncomeCheckpoint(
					checkpoint
				)
				break
			case 'PropertyOwnership':
				this.propertyOwnershipCheckpoint = new models.FinancialModels.PropertyOwnershipCheckpoint(
					checkpoint
				)
				break
			case models.CheckpointType.PublicTrusteeship:
				this.publicTrusteeshipCheckpoint = new models.FinancialModels.PublicTrusteeshipCheckpoint(
					checkpoint
				)
				break
			case models.CheckpointType.PartnerBackgroundCheck:
				this.partnerBackgroundCheckCheckpoint = new models.FinancialModels.PartnerBackgroundCheckCheckpoint(
					checkpoint
				)
				break
			case models.CheckpointType.RepresentationProhibition:
				this.representationProhobition = new models.FinancialModels.PublicTrusteeshipCheckpoint(
					checkpoint
				)
				break
			case models.CheckpointType.BeneficialOwner:
				this.beneficialOwnerCheckpoint = new models.FinancialModels.BeneficialOwnerCheckpoint(
					checkpoint
				)
				break
			case models.CheckpointType.ExtendedCorporateEngagementMapping:
				this.extendedCorporateEngagementMappingCheckpoint = new models.FinancialModels.ExtendedCorporateEngagementMappingCheckpoint(
					checkpoint
				)
				break
			case 'Employment':
				this.employmentCheckpoint = new models.ResumeModels.EmploymentCheckpoint(
					checkpoint
				)
				break
			case 'CurrentEmployment':
				this.currentEmploymentCheckpoint = new models.ResumeModels.EmploymentCheckpoint(
					checkpoint
				)
				break
			case 'HighestEducation':
				this.highestEducationCheckpoint = new models.ResumeModels.EducationCheckpoint(
					checkpoint
				)
				break
			case 'Educations':
				this.educationCheckpoint = new models.ResumeModels.EducationCheckpoint(
					checkpoint
				)
				break
			case 'ReferencePerson':
				this.referencesCheckpoint = new models.ResumeModels.ReferencesCheckpoint(
					checkpoint
				)
				break
			case 'OtherMerits':
				this.otherMeritsCheckpoint = new models.ResumeModels.OtherMeritsCheckpoint(
					checkpoint
				)
				break
			case 'SoleProprietorship':
				this.soleproprietorshipCheckpoint = new models.FinancialModels.SoleProprietorshipCheckpoint(
					checkpoint
				)
				break
			case 'InternetAndMedia':
				this.internetAndMediaCheckpoint = new models.MediaModels.InternetAndMediaCheckpoint(
					checkpoint
				)
				break
			default:
				break
		}
	}

	@action changeLanguage() {
		const currentLanguage = this.language
		if (currentLanguage !== 'English') {
			this.language = 'English'
		}
		else {
			this.language = 'Svenska'
		}
	}

	@computed get showLanguageSwitch(): boolean {
		//Visa endast i förhandsgranskningen. Ej i de riktiga portalerna.
		return !this.userStore.user.companyId
	}

	@computed get hasCheckpointData(): boolean {
		return !this.order.isInternationalOrder && this.hasCollectedCheckpoints
	}
}
